import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef, type RefObject } from 'react'

import type { VisibleRows } from '@components/ui/InfiniteTable/hooks/useVisibleRows'
import {
  classifyPayload,
  getIdFromRecord,
  handleBatchResponse,
  handleResourceKeyResponse,
  partitionByVisibility,
  type ID,
} from '@features/websocket/lib/socketEventStrategies'
import { replaceCollectionInQueries, updateRowInQueries, type Events } from '@features/websocket/lib/updateRowInQueries'
import { socketEmit, socketLog, socketOn, type WSPayload } from '@features/websocket/socketService'

export type { ID } from '@features/websocket/lib/socketEventStrategies'
export type RequiredToUpdateDict = { [key: ID]: boolean }
type SocketRow = Record<string, unknown>

/**
 * Helper that listens to socket events and patches the TanStack
 * Query cache for queries with `baseKey` prefix.
 *
 * socketName - the name of the socket to listen to
 * baseKey - the base key of the query to patch
 * event - the event to listen to
 * - on 'message' event, we requesting data from the server for the given id
 * - on other events ['updated', 'deleted', 'created'], we updating the query cache via `updateRowInQueries`
 * mapPayload - the function to map the payload to the row
 * idKey - the key of the id in the payload (default is 'id')
 * visibleRows - the visible rows to update
 * requiredToUpdateDictRef - the reference to the dictionary of required to update rows
 *
 * Implementation strategy (two-step update model):
 *  1) Listen for lightweight notifications (e.g. `{ event: 'updated', id }`).
 *     These are small and cheap to broadcast.
 *  2) On notification, ask the server for the full resource by sending
 *     `retrieve_resource` over the same channel (server then responds with full payload).
 *  3) Listen for `message` events containing the full serialized resource and
 *     patch the cache via `updateRowInQueries` or invalidate queries if requested.
 *
 * This keeps notifications small and ensures clients always receive a consistent
 * full resource before applying cache updates.
 *
 * Example:
 *   useRealtimeTableSocket({
 *     socketName: 'tasks',
 *     baseKey: ['tasks'],
 *     event: 'updated', //'created', 'deleted', 'updated', "*"
 *     mapPayload: p => p.data ?? p,
 *   })
 */
export type UseRealtimeTableSocketProps<T extends SocketRow = SocketRow> = {
  socketName: string
  baseKey: readonly unknown[]
  mapPayload?: (payload: WSPayload) => T
  isBatch?: boolean
  idKey?: string
  /**
   * Key in the payload that wraps the collection returned by `retrieve_resources`.
   * When the server responds to `retrieve_resources`, it sends `{ [resourceKey]: [...rows] }`
   * without an `event` field. This tells the hook how to unwrap and patch each row.
   */
  resourceKey?: string
  visibleRows?: VisibleRows<T>
  requiredToUpdateDictRef?: RefObject<RequiredToUpdateDict>
}

export function useRealtimeTableSocket<T extends SocketRow = SocketRow>({
  socketName,
  baseKey,
  mapPayload,
  isBatch = false,
  idKey = 'id',
  resourceKey,
  visibleRows,
  requiredToUpdateDictRef,
}: UseRealtimeTableSocketProps<T>) {
  const qc = useQueryClient()
  const searchParams = useSearchParams()
  // Keep a stable reference to visibleRows to prevent socket handler re-subscriptions on scroll
  const visibleRowsRef = useRef(visibleRows)
  useEffect(() => {
    visibleRowsRef.current = visibleRows
  }, [visibleRows])

  // New data should !NOT! be added to the collection if filters are enabled
  const isFiltersEnabled = searchParams.size > 0

  const setIsRequiredToUpdate = useCallback(
    (id: ID, value: boolean) => {
      if (!requiredToUpdateDictRef?.current) return
      requiredToUpdateDictRef.current[id] = value
    },
    [requiredToUpdateDictRef],
  )

  const isVisibleRow = useCallback(
    (id: ID) => {
      // If visibleRows is not provided (e.g. on Detail pages), we assume it is always visible
      if (!visibleRowsRef.current) return true

      // Use string comparison to avoid type mismatches (number vs string)
      return visibleRowsRef.current.some((row) => String(getIdFromRecord(row.original, idKey)) === String(id))
    },
    [idKey],
  )

  const updateRow = useCallback(
    (data: SocketRow, payloadEvent: Events) =>
      updateRowInQueries(qc, baseKey, data, { idKey }, payloadEvent, isFiltersEnabled),
    [qc, baseKey, idKey, isFiltersEnabled],
  )

  const requestResourceById = useCallback(
    (id: ID) => {
      try {
        socketLog(`>>>>>>>>>> VISIBLE and updateRowById ${id}`)
        setIsRequiredToUpdate(id, false)
        socketEmit(socketName, 'retrieve_resource', { id })
      } catch (e) {
        console.error('socket request for resource failed', e)
      }
    },
    [socketName, setIsRequiredToUpdate],
  )

  const requestResourcesByIds = useCallback(
    (ids: ID[]) => {
      try {
        socketLog(`>>>>>>>>>> updateRowsByIds [${ids.join(', ')}]`)
        ids.forEach((id) => setIsRequiredToUpdate(id, false))
        socketEmit(socketName, 'retrieve_resources', { ids_to_be_refetched: ids })
      } catch (e) {
        console.error('socket request for resources failed', e)
      }
    },
    [socketName, setIsRequiredToUpdate],
  )

  const getPayloadData = useCallback(
    (payload: WSPayload) => (mapPayload ? mapPayload(payload) : payload?.data),
    [mapPayload],
  )

  const socketEventHandler = useCallback(
    (payload: WSPayload) => {
      // 1. Handle `retrieve_resources` bulk response: `{ [resourceKey]: [...rows] }`
      if (handleResourceKeyResponse(payload, resourceKey, mapPayload, updateRow)) {
        socketLog(`socketEventHandler ${socketName} retrieve_resources response via "${resourceKey}"`)

        return
      }

      // 2. Handle batch mode (replaces entire collection)
      if (isBatch) {
        const rows = handleBatchResponse(payload, mapPayload)
        if (rows) {
          replaceCollectionInQueries(qc, baseKey, rows)

          return
        }
      }

      // 3. Extract event, data, and ID
      const payloadEvent = payload?.event
      const data = getPayloadData(payload)
      const id = getIdFromRecord(data, idKey)
      if (!id) return

      socketLog(`socketEventHandler ${socketName} ${payloadEvent} (ID: ${id})`)

      // 4. Deletions don't need full resource
      if (payloadEvent === 'deleted') {
        if (typeof data === 'object' && data !== null) {
          updateRow(data as SocketRow, payloadEvent)
        }

        return
      }

      // 5. Classify: full data vs lightweight notification
      const { isFullData, idsToRefetch } = classifyPayload(data, idKey, payloadEvent)
      const isCreatedEvent = payloadEvent === 'created'

      if (isFullData && typeof data === 'object' && data !== null) {
        setIsRequiredToUpdate(id, false)
        updateRow(data as SocketRow, payloadEvent!)

        return
      }

      // 6. Lightweight notification — request full data from server
      if (idsToRefetch) {
        const { visibleIds, nonVisibleIds } = partitionByVisibility(idsToRefetch, isVisibleRow, isCreatedEvent)

        if (visibleIds.length > 0) {
          requestResourcesByIds(visibleIds)
        }
        nonVisibleIds.forEach((refetchId) => {
          socketLog(`>>>>>>>>>> ${refetchId} NOT VISIBLE, SKIPPING`)
          setIsRequiredToUpdate(refetchId, true)
        })
      } else if (isVisibleRow(id) || isCreatedEvent) {
        requestResourceById(id)
      } else {
        socketLog(`>>>>>>>>>> ${id} NOT VISIBLE, SKIPPING`)
        setIsRequiredToUpdate(id, true)
      }
    },
    [
      socketName,
      resourceKey,
      mapPayload,
      updateRow,
      isBatch,
      getPayloadData,
      idKey,
      setIsRequiredToUpdate,
      isVisibleRow,
      requestResourceById,
      requestResourcesByIds,
      qc,
      baseKey,
    ],
  )

  // Update rows that were marked while not visible, once they scroll into view
  useEffect(() => {
    visibleRows?.forEach((row) => {
      const id = getIdFromRecord(row.original, idKey)
      if (!id) return

      if (requiredToUpdateDictRef?.current?.[id]) {
        requestResourceById(id)
      }
    })
  }, [visibleRows, requestResourceById, requiredToUpdateDictRef, idKey])

  // Keep the latest handler in a ref so the subscription registers ONCE per socketName
  const socketEventHandlerRef = useRef(socketEventHandler)

  useEffect(() => {
    socketEventHandlerRef.current = socketEventHandler
  }, [socketEventHandler])

  useEffect(() => {
    const unsub = socketOn(socketName, '*', (payload) => socketEventHandlerRef.current(payload))

    return () => unsub()
  }, [socketName])
}
